/**
 * Aba "Manual Foto Modelo" da ficha da cliente, idêntica à do legado (`admin.html:2810-2814`,
 * `5244-5271`; linha 220 do inventário).
 *
 * Só leitura: a cliente preenche no portal dela. Mostra as quatro perguntas respondidas (só as
 * preenchidas, na ordem fixa) e a foto enviada, com link assinado de uma hora.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaManualFoto`.
 */

// Estado e memória de função do React.
import { useCallback, useState } from 'react';
// A leitura das respostas e o link assinado da foto.
import { lerManualFoto } from '@studiolu/dados/areas/ficha-manual-foto';
import { gerarLinkAssinado } from '@studiolu/dados/areas/ficha-arquivos';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O contrato da aba.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// As peças da A3.
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

/** As quatro perguntas, na ordem e com o texto do legado (`admin.html:5252-5257`). */
const PERGUNTAS_MANUAL_FOTO: readonly (readonly [campo: CampoDoManual, pergunta: string])[] = [
  ['cabelo_coloracao_natural', 'O seu cabelo está na coloração natural?'],
  ['micropigmentacao_sobrancelha', 'Você tem micropigmentação na sobrancelha?'],
  ['cilios_posticos', 'Você tem cílios postiços?'],
  ['o_que_incomoda_rosto', 'O que mais incomoda no seu rosto?'],
];

/** As colunas de texto que viram pergunta. */
type CampoDoManual = 'cabelo_coloracao_natural' | 'micropigmentacao_sobrancelha' | 'cilios_posticos' | 'o_que_incomoda_rosto';

/** O que a aba desenha depois de ler. */
type Estado =
  | { readonly tipo: 'carregando' }
  | { readonly tipo: 'sem-resposta' }
  | {
      readonly tipo: 'respostas';
      readonly linhas: readonly { readonly pergunta: string; readonly valor: string }[];
      // A foto, quando há `foto_caminho`: o link (ou `null` se falhou) e o texto do link.
      readonly foto: { readonly link: string | null; readonly nome: string } | null;
    };

export function AbaManualFoto({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Manual Foto Modelo" />;
  return <ConteudoManualFoto ficha={ficha} />;
}

function ConteudoManualFoto({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { veLinksDeAdministrador } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });

  // A leitura da aba (`carregarManualFotoAdmin`, `admin.html:5245-5271`), pelo contrato de carga:
  // a moldura a roda antes de mostrar a aba, e de novo a cada toque no botão dela.
  const carregar = useCallback(async () => {
    setEstado({ tipo: 'carregando' });
    const resultado = await lerManualFoto(dados, escopo);
    // DEFEITO DO LEGADO REPRODUZIDO (achado da A3, fora da seção 4): a falha na leitura só vai
    // para o console e a tela diz "Cliente ainda não respondeu.", como se não houvesse resposta
    // (`admin.html:5249-5250`).
    if (!resultado.ok) console.error('Erro ao carregar manual foto:', resultado.erro.mensagem);
    const linha = resultado.ok ? resultado.dados : null;
    if (!linha) {
      setEstado({ tipo: 'sem-resposta' });
      return;
    }
    // Só as perguntas com resposta, na ordem fixa (`if (!data[campo]) return;`, `5261`).
    const linhas = PERGUNTAS_MANUAL_FOTO.filter(([campo]) => Boolean(linha[campo])).map(([campo, pergunta]) => ({
      pergunta,
      valor: linha[campo] ?? '',
    }));
    // A foto: link assinado de uma hora; a falha do link é ignorada, como no legado (`5266`).
    let foto: { link: string | null; nome: string } | null = null;
    if (linha.foto_caminho) {
      const link = await gerarLinkAssinado(dados, linha.foto_caminho);
      foto = { link: link.ok && link.dados ? link.dados.signedUrl : null, nome: linha.foto_nome || 'Abrir foto' };
    }
    setEstado({ tipo: 'respostas', linhas, foto });
  }, [dados, escopo]);
  useCarregadorDaAba(carregar);

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Manual Foto Modelo</h3>
      <p className="text-cx-sm text-texto-suave">Preenchido pela cliente no portal dela. Aqui é só leitura.</p>
      <div data-testid="corpo-manual-foto" className="flex flex-col gap-cx-2">
        {estado.tipo === 'carregando' && <p className="text-cx-sm text-texto-suave">Carregando…</p>}
        {estado.tipo === 'sem-resposta' && <p className="text-cx-sm text-texto-suave">Cliente ainda não respondeu.</p>}
        {estado.tipo === 'respostas' && estado.linhas.length === 0 && estado.foto === null && (
          // Linha existe e veio toda vazia (`admin.html:5270`).
          <p className="text-cx-sm text-texto-suave">Cliente respondeu, mas todos os campos vieram vazios.</p>
        )}
        {estado.tipo === 'respostas' &&
          estado.linhas.map((linha) => (
            <div key={linha.pergunta} className="flex flex-col gap-cx-1 border-b border-borda py-cx-2">
              <div className="text-cx-sm font-medium text-texto">{linha.pergunta}</div>
              <div className="text-cx-sm text-texto-suave">{linha.valor}</div>
            </div>
          ))}
        {estado.tipo === 'respostas' && estado.foto !== null && (
          <div className="flex flex-col gap-cx-1 border-b border-borda py-cx-2">
            <div className="text-cx-sm font-medium text-texto">Foto enviada</div>
            <div>
              {/* Link que falhou vira `#`, e `a[href="#"]` some para quem não é administrador (`3375`). */}
              {(estado.foto.link !== null || veLinksDeAdministrador) && (
                <a
                  href={estado.foto.link ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-toque items-center break-all text-cx-sm text-marca underline"
                >
                  {estado.foto.nome}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
